import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/admin/layout/AdminLayout';
import { adminService } from '../../../services/admin/adminService';

const AdminSubscriptions = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    package: '',
    status: '',
    search: ''
  });
  
  const [pagination, setPagination] = useState({});

  // Subscription packages data
  const subscriptionPackages = [
    {
      id: "1",
      name: "Basic",
      description: "Basic subscription package",
      price: 100,
      features: {
        stuff: 2,
        players: 10,
        teams: 2,
        leagues: 0,
        events: 2,
        matches: 1,
        competitions: 0,
      },
      duration: "365",
    },
    {
      id: "2",
      name: "Pro",
      description: "Pro subscription package",
      price: 200,
      features: {
        stuff: 10,
        players: 90,
        teams: 6,
        leagues: 1,
        events: 6,
        matches: 5,
        competitions: 2,
      },
      duration: "365",
    },
    {
      id: "3",
      name: "Enterprise",
      description: "Enterprise subscription package",
      price: 300,
      features: {
        stuff: 30,
        players: 300,
        teams: 15,
        leagues: 12,
        events: -1,
        matches: -1,
        competitions: 10,
      },
      duration: "365",
    },
  ];

  useEffect(() => {
    fetchSubscriptions();
  }, [filters]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      
      console.log('🔍 Fetching subscriptions with filters:', filters);
      
      const response = await adminService.getAllSubscriptions(filters);
      
      console.log('📊 Received subscription data:', {
        total: response.data.data.subscriptions?.length || 0,
        pagination: response.data.data.pagination,
        filters: response.data.data.filters,
        sampleSubscription: response.data.data.subscriptions?.[0]
      });
      
      setSubscriptions(response.data.data.subscriptions || []);
      setPagination(response.data.data.pagination || {});
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      setError('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    console.log(`🔄 Filter changed: ${key} = ${value}`);
    
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const getPackageInfo = (packageId) => {
    return subscriptionPackages.find(pkg => pkg.id === String(packageId)) || subscriptionPackages[0];
  };

  const getStatusBadge = (subscriptionData) => {
    const subscription = subscriptionData.subscription;
    
    // First check if there's an explicit status field
    if (subscription.status) {
      const statusStyles = {
        active: 'bg-green-100 text-green-800',
        inactive: 'bg-gray-100 text-gray-800',
        expired: 'bg-red-100 text-red-800',
        suspended: 'bg-yellow-100 text-yellow-800',
        pending: 'bg-blue-100 text-blue-800'
      };
      
      const className = statusStyles[subscription.status] || statusStyles.inactive;
      return <span className={`px-2 py-1 text-xs font-medium rounded-full ${className}`}>
        {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
      </span>;
    }
    
    // Fallback to old logic for backward compatibility
    // Check for pending payment first
    if (subscription.hasPendingPaymentTransaction) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">Pending Payment</span>;
    }
    
    if (!subscription.isActive) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Inactive</span>;
    }
    
    // Handle both timestamp and ISO date formats
    const endDate = new Date(typeof subscription.endDate === 'number' ? subscription.endDate : subscription.endDate);
    const now = new Date();
    
    if (endDate < now || subscription.isExpired) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Expired</span>;
    }
    
    const daysLeft = subscription.daysRemaining || Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 30) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">Expiring Soon</span>;
    }
    
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Active</span>;
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    // Handle both timestamp numbers and ISO date strings
    const date = new Date(typeof dateValue === 'number' ? dateValue : dateValue);
    return date.toLocaleDateString();
  };

  const formatFeatures = (features) => {
    if (!features) return 'N/A';
    return Object.entries(features)
      .filter(([key, value]) => value !== 0)
      .map(([key, value]) => `${key}: ${value === -1 ? '∞' : value}`)
      .join(', ');
  };

  const getStatsData = () => {
    const total = subscriptions.length;
    
    const active = subscriptions.filter(item => {
      const sub = item.subscription;
      
      // Check status field first
      if (sub.status) {
        return sub.status === 'active';
      }
      
      // Fallback to old logic
      const endDate = new Date(typeof sub.endDate === 'number' ? sub.endDate : sub.endDate);
      return sub.isActive && endDate > new Date() && !sub.isExpired && !sub.hasPendingPaymentTransaction;
    }).length;
    
    const expiringSoon = subscriptions.filter(item => {
      const sub = item.subscription;
      
      // For new status system, expired status means expired
      if (sub.status) {
        return sub.status === 'expired';
      }
      
      // Fallback to old logic
      if (!sub.isActive || sub.isExpired || sub.hasPendingPaymentTransaction) return false;
      const daysLeft = sub.daysRemaining || 0;
      return daysLeft <= 30 && daysLeft > 0;
    }).length;

    const pendingPayment = subscriptions.filter(item => {
      const sub = item.subscription;
      
      // Check status field first
      if (sub.status) {
        return sub.status === 'pending';
      }
      
      // Fallback to old logic
      return sub.hasPendingPaymentTransaction;
    }).length;

    const totalRevenue = subscriptions.reduce((sum, item) => {
      const sub = item.subscription;
      let isActiveSubscription = false;
      
      // Determine if subscription is active for revenue calculation
      if (sub.status) {
        isActiveSubscription = sub.status === 'active';
      } else {
        // Fallback to old logic
        const endDate = new Date(typeof sub.endDate === 'number' ? sub.endDate : sub.endDate);
        isActiveSubscription = sub.isActive && endDate > new Date() && !sub.isExpired;
      }
      
      if (isActiveSubscription) {
        // Use packagePrice if available, otherwise fallback to package info
        const price = sub.packagePrice || getPackageInfo(sub.package).price;
        return sum + (price || 0);
      }
      return sum;
    }, 0);

    return { total, active, expiringSoon, pendingPayment, totalRevenue };
  };

  const stats = getStatsData();
  
  // Debug stats calculation
  console.log('📈 Stats calculated:', {
    total: stats.total,
    active: stats.active,
    expiringSoon: stats.expiringSoon,
    pendingPayment: stats.pendingPayment,
    totalRevenue: stats.totalRevenue,
    subscriptionsCount: subscriptions.length,
    sampleSubscriptionStatus: subscriptions[0]?.subscription?.status,
    sampleSubscriptionIsActive: subscriptions[0]?.subscription?.isActive
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Subscription Management</h1>
            <p className="text-gray-600 mt-1">Manage organization subscriptions and packages</p>
          </div>
          <button
            onClick={() => navigate('/admin/subscriptions/create')}
            className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            Create Subscription
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setError('')}
                  className="inline-flex text-red-400 hover:text-red-600"
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Subscriptions</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.total}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Active Subscriptions</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.active}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Pending Payment</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.pendingPayment}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Revenue</dt>
                    <dd className="text-lg font-medium text-gray-900">${stats.totalRevenue.toLocaleString()}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Filters</h2>
          </div>
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  placeholder="Search by organization..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Package</label>
                <select
                  value={filters.package}
                  onChange={(e) => handleFilterChange('package', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">All Packages</option>
                  {subscriptionPackages.map(pkg => (
                    <option key={pkg.id} value={pkg.id}>{pkg.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="expired">Expired</option>
                  <option value="suspended">Suspended</option>
                  <option value="pending">Pending</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Per Page</label>
                <select
                  value={filters.limit}
                  onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Subscriptions Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-white bg-orange-500 transition ease-in-out duration-150">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading subscriptions...
              </div>
            </div>
          ) : subscriptions.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No subscriptions</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating a new subscription.</p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/admin/subscriptions/create')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                >
                  Create Subscription
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Organization
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Package
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dates
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Features
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {subscriptions.map((subscriptionData) => {
                      const subscription = subscriptionData.subscription;
                      // Use packageFeatures if available, otherwise get from packageInfo
                      const packageInfo = subscription.packageFeatures ? 
                        { 
                          name: subscription.packageName || getPackageInfo(subscription.package).name,
                          price: subscription.packagePrice || getPackageInfo(subscription.package).price,
                          features: subscription.packageFeatures 
                        } : 
                        getPackageInfo(subscription.package);
                      
                      return (
                        <tr key={subscriptionData.userId} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                {subscriptionData.profilePicture ? (
                                  <img 
                                    src={subscriptionData.profilePicture} 
                                    alt={subscriptionData.fullName || subscriptionData.userName}
                                    className="h-10 w-10 rounded-full object-cover"
                                    onError={(e) => {
                                      // Fallback to initials if image fails to load
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div className={`h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center ${subscriptionData.profilePicture ? 'hidden' : ''}`}>
                                  <span className="text-orange-600 font-medium text-sm">
                                    {(subscriptionData.fullName || subscriptionData.userName || 'O').charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {subscriptionData.fullName || 'Unknown Organization'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {subscriptionData.email || 'No email'}
                                </div>
                                <div className="text-xs text-gray-400">
                                  @{subscriptionData.userName}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{packageInfo.name}</div>
                            <div className="text-sm text-gray-500">${packageInfo.price}/year</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(subscriptionData)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div>Start: {formatDate(subscription.startDate)}</div>
                            <div>End: {formatDate(subscription.endDate)}</div>
                            {subscription.daysRemaining && (
                              <div className="text-xs text-gray-500 mt-1">
                                {subscription.daysRemaining} days remaining
                              </div>
                            )}
                            {subscription.paymentTransactionStatus && (
                              <div className={`text-xs mt-1 ${
                                subscription.paymentTransactionStatus === 'completed' ? 'text-green-600' :
                                subscription.paymentTransactionStatus === 'pending' ? 'text-yellow-600' :
                                'text-red-600'
                              }`}>
                                Payment: {subscription.paymentTransactionStatus}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            <div className="max-w-xs truncate">
                              {formatFeatures(packageInfo.features)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => navigate(`/admin/subscriptions/edit/${subscriptionData.userId}`)}
                              className="text-orange-600 hover:text-orange-900"
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="bg-white px-4 py-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={!pagination.hasPrev}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={!pagination.hasNext}
                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Showing{' '}
                          <span className="font-medium">
                            {((pagination.page - 1) * pagination.limit) + 1}
                          </span>{' '}
                          to{' '}
                          <span className="font-medium">
                            {Math.min(pagination.page * pagination.limit, pagination.total)}
                          </span>{' '}
                          of{' '}
                          <span className="font-medium">{pagination.total}</span> results
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                          <button
                            onClick={() => handlePageChange(pagination.page - 1)}
                            disabled={!pagination.hasPrev}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>
                          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((page) => (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                page === pagination.page
                                  ? 'z-10 bg-orange-50 border-orange-500 text-orange-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          ))}
                          <button
                            onClick={() => handlePageChange(pagination.page + 1)}
                            disabled={!pagination.hasNext}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSubscriptions; 